// custom typefaces
import "typeface-montserrat";
import "typeface-merriweather";
// normalize CSS across browsers
import "./src/normalize.css";
// custom CSS styles
import "./src/style.scss";

// Highlighting for code blocks
import "prismjs/themes/prism.css";
// Track page navigation for referrer information
let previousPage = '';

// Function to detect traffic source based on referrer
function detectTrafficSource(referrer) {
    if (!referrer) return '';
    
    const referrerDomain = new URL(referrer).hostname.toLowerCase();
    
    // Search engines
    if (referrerDomain.includes('google.')) return 'Google';
    if (referrerDomain.includes('bing.')) return 'Bing';
    if (referrerDomain.includes('yahoo.')) return 'Yahoo';
    if (referrerDomain.includes('duckduckgo.')) return 'DuckDuckGo';
    if (referrerDomain.includes('baidu.')) return 'Baidu';
    if (referrerDomain.includes('yandex.')) return 'Yandex';
    
    // Social media
    if (referrerDomain.includes('facebook.')) return 'Facebook';
    if (referrerDomain.includes('instagram.')) return 'Instagram';
    if (referrerDomain.includes('twitter.') || referrerDomain.includes('x.com')) return 'Twitter/X';
    if (referrerDomain.includes('linkedin.')) return 'LinkedIn';
    if (referrerDomain.includes('youtube.')) return 'YouTube';
    if (referrerDomain.includes('tiktok.')) return 'TikTok';
    if (referrerDomain.includes('pinterest.')) return 'Pinterest';
    if (referrerDomain.includes('snapchat.')) return 'Snapchat';
    
    // Other common sources
    if (referrerDomain.includes('reddit.')) return 'Reddit';
    if (referrerDomain.includes('tripadvisor.')) return 'TripAdvisor';
    if (referrerDomain.includes('booking.')) return 'Booking.com';
    if (referrerDomain.includes('expedia.')) return 'Expedia';
    if (referrerDomain.includes('agoda.')) return 'Agoda';
    
    // Return the domain name if no specific match
    return referrerDomain;
}

// Function to detect organic source based on referrer
function detectOrganicSource(referrer) {
    if (!referrer) return '';
    
    const referrerDomain = new URL(referrer).hostname.toLowerCase();
    
    // Check if it's a search engine (organic traffic)
    if (referrerDomain.includes('google.') || 
        referrerDomain.includes('bing.') || 
        referrerDomain.includes('yahoo.') || 
        referrerDomain.includes('duckduckgo.') || 
        referrerDomain.includes('baidu.') || 
        referrerDomain.includes('yandex.')) {
        return 'Search Engine';
    }
    
    // Check if it's social media
    if (referrerDomain.includes('facebook.') || 
        referrerDomain.includes('instagram.') || 
        referrerDomain.includes('twitter.') || 
        referrerDomain.includes('x.com') || 
        referrerDomain.includes('linkedin.') || 
        referrerDomain.includes('youtube.') || 
        referrerDomain.includes('tiktok.') || 
        referrerDomain.includes('pinterest.') || 
        referrerDomain.includes('snapchat.')) {
        return 'Social Media';
    }
    
    // Check if it's a travel/booking site
    if (referrerDomain.includes('tripadvisor.') || 
        referrerDomain.includes('booking.') || 
        referrerDomain.includes('expedia.') || 
        referrerDomain.includes('agoda.')) {
        return 'Travel Site';
    }
    
    // Check if it's a forum/community
    if (referrerDomain.includes('reddit.')) {
        return 'Forum/Community';
    }
    
    // Default to "Direct" for other external sources
    return 'Direct';
}

export const onRouteUpdate = ({ location }) => {
    if (previousPage && previousPage !== location.pathname) {
        sessionStorage.setItem('previous_page', previousPage);
    }
    previousPage = location.pathname;
};

export const onInitialClientRender = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const utmSource   = searchParams.get('utm_source')   || '';
    const utmMedium   = searchParams.get('utm_medium')   || '';
    const utmCampaign = searchParams.get('utm_campaign') || '';
    const utmTerm     = searchParams.get('utm_term')     || '';
    const utmContent  = searchParams.get('utm_content')  || '';
    const gclid       = searchParams.get('gclid')        || '';
    const trafficSource = searchParams.get('traffic_source') || '';
    const organic     = searchParams.get('organic') || '';
    const referrer    = searchParams.get('referrer') || '';

    const documentReferrer = document.referrer || '';

    if (utmSource)   sessionStorage.setItem('utm_source',   utmSource);
    if (utmMedium)   sessionStorage.setItem('utm_medium',   utmMedium);
    if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);
    if (utmTerm)     sessionStorage.setItem('utm_term',     utmTerm);
    if (utmContent)  sessionStorage.setItem('utm_content',  utmContent);
    if (gclid)       sessionStorage.setItem('gclid',        gclid);
    if (trafficSource) sessionStorage.setItem('traffic_source', trafficSource);
    if (organic)     sessionStorage.setItem('organic',      organic);
    if (referrer)    sessionStorage.setItem('referrer',     referrer);

    // gclid implies Google / CPC — auto-populate if not already set
    if (gclid) {
        if (!sessionStorage.getItem('traffic_source')) sessionStorage.setItem('traffic_source', 'Google');
        if (!sessionStorage.getItem('organic'))        sessionStorage.setItem('organic', 'Search Engine');
    }

    if (!trafficSource && utmSource) {
        sessionStorage.setItem('traffic_source', utmSource.charAt(0).toUpperCase() + utmSource.slice(1));
    }

    if (!organic && utmMedium) {
        const mediumMap = {
            organic: 'Search Engine', cpc: 'Search Engine', ppc: 'Search Engine',
            social: 'Social Media', facebook: 'Social Media', instagram: 'Social Media',
            twitter: 'Social Media', linkedin: 'Social Media', youtube: 'Social Media',
            tiktok: 'Social Media', pinterest: 'Social Media', snapchat: 'Social Media',
            email: 'Email', referral: 'Referral', display: 'Display', banner: 'Display',
        };
        const derived = mediumMap[utmMedium.toLowerCase()]
            || utmMedium.charAt(0).toUpperCase() + utmMedium.slice(1);
        sessionStorage.setItem('organic', derived);
    }

    if (documentReferrer && !documentReferrer.startsWith(window.location.origin)) {
        sessionStorage.setItem('document_referrer', documentReferrer);

        const detectedTrafficSource = detectTrafficSource(documentReferrer);
        const detectedOrganicSource = detectOrganicSource(documentReferrer);

        if (!trafficSource && detectedTrafficSource) sessionStorage.setItem('traffic_source', detectedTrafficSource);
        if (!organic && detectedOrganicSource)       sessionStorage.setItem('organic',         detectedOrganicSource);
    }

    previousPage = window.location.pathname;
};